class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this._setupElements();
    this._initializeHandlers();

    this._setupLimitedControls();
    this._updateState();
  }

  disconnectedCallback() {
    if (!this._handlers) return;

    this._removeEventListeners();
  }

  _setupElements() {
    this.input = this.querySelector('input[type="number"]');
    this.incBtn = this.querySelector('[data-qty-btn="inc"]');
    this.decBtn = this.querySelector('[data-qty-btn="dec"]');
    this.maxMessage = this.querySelector('.quantity-max-message');
    this.maxAllowed = parseInt(this.dataset.maxAllowed, 10);
    this.errorMessage = document.querySelector('.quantity-error-message');
    this.closeBtn = document.querySelector('[data-action="close-qty-message"]');
    this.hideTimeout = null;
  }

  _initializeHandlers() {
    this._handlers = {
      inc: null,
      dec: null,
      input: null,
      closeMessage: null
    };
    
    // Setup close button handler
    if (this.closeBtn) {
      this._handlers.closeMessage = () => this._hideErrorMessage();
      this.closeBtn.addEventListener('click', this._handlers.closeMessage);
    }
  }

  _setupBasicControls() {
    if (!this.input) return;

    this._handlers.inc = () => this.input.stepUp();
    this._handlers.dec = () => this.input.stepDown();

    if (this.incBtn) this.incBtn.addEventListener('click', this._handlers.inc);
    if (this.decBtn) this.decBtn.addEventListener('click', this._handlers.dec);
  }

  _setupLimitedControls() {
    this._handlers.inc = () => this._handleIncrement();
    this._handlers.dec = () => this._handleDecrement();
    this._handlers.input = () => this._handleInput();

    if (this.incBtn) this.incBtn.addEventListener('click', this._handlers.inc);
    if (this.decBtn) this.decBtn.addEventListener('click', this._handlers.dec);
    if (this.input) this.input.addEventListener('input', this._handlers.input);
  }

  _handleIncrement() {
    if (!this.input) return;

    this.input.stepUp();
    this._updateState();
  }

  _handleDecrement() {
    if (!this.input) return;

    this.input.stepDown();
    this._updateState();
  }

  _handleInput() {
    if (!this.input) return;

    const currentValue = parseInt(this.input.value, 10);
    if (currentValue > this.maxAllowed) {
      this.input.value = this.maxAllowed;
      this._showErrorMessage();
    } else {
      this._hideErrorMessage();
    }

    this._updateState();
  }

  _updateState() {
    if (!this.input) return;

    const atMax = parseInt(this.input.value, 10) >= this.maxAllowed;

    this._updateIncrementButton(atMax);
    this._updateMaxMessage(atMax);
  }

  _updateIncrementButton(atMax) {
    if (!this.incBtn) return;

    this.incBtn.style.cursor = atMax ? 'not-allowed' : 'pointer';

    const svg = this.incBtn.querySelector('svg');
    if (!svg) return;

    svg.style.opacity = atMax ? '0.6' : '1';
  }

  _updateMaxMessage(atMax) {
    if (!this.maxMessage) return;

    this.maxMessage.classList.toggle('hidden', !atMax);
  }

  _showErrorMessage() {
    if (!this.errorMessage) return;
    
    // Clear any existing timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    
    this.errorMessage.style.display = 'block';
    
    // Auto-hide after 5 seconds
    this.hideTimeout = setTimeout(() => {
      this._hideErrorMessage();
    }, 5000);
  }

  _hideErrorMessage() {
    if (!this.errorMessage) return;
    
    // Clear timeout if manually closing
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    this.errorMessage.style.display = 'none';
  }

  _removeEventListeners() {
    if (this.incBtn && this._handlers.inc) {
      this.incBtn.removeEventListener('click', this._handlers.inc);
    }

    if (this.decBtn && this._handlers.dec) {
      this.decBtn.removeEventListener('click', this._handlers.dec);
    }

    if (this.input && this._handlers.input) {
      this.input.removeEventListener('input', this._handlers.input);
    }

    if (this.closeBtn && this._handlers.closeMessage) {
      this.closeBtn.removeEventListener('click', this._handlers.closeMessage);
    }
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}
